/**
 * Contrato da visão 360 (Task 13, brief 3.4) — os tipos que o **runtime
 * persistente** publica e a **tela** consome, definidos UMA vez.
 *
 * Endereço CANÔNICO (Task 13, limpeza de arquitetura): promovido de
 * `apps/plataforma/src/crm/contract.ts` para `packages/crm`. A Edge Function
 * `supabase/functions/crm-runtime` e o app importam daqui; o arquivo antigo em
 * `apps/plataforma/src/crm/contract.ts` reexporta deste módulo.
 *
 * Por que este módulo existe (correção da revisão da Iteração 2, finding 1/2):
 *
 * 1. O limite persistente do CRM mora em `supabase/functions/crm-runtime/`
 *    (invariante 4 do `ARCHITECTURE-MAP`: persistência real só lá). A tela
 *    mora em `apps/plataforma`. Os dois precisam do MESMO formato de payload,
 *    e um contrato duplicado nas duas pontas é a forma mais barata de a tela
 *    exibir um campo que o servidor nunca publicou.
 * 2. O payload de `/api/crm/360` é o que torna a visão 360 **real**: ele já
 *    chega filtrado pelo cliente CANÔNICO resolvido por
 *    `resolve_customer_by_identity` (migration 0020), em vez de a tela filtrar
 *    seis listas globais por conta própria. A agregação (totais, segmentos,
 *    prazos) continua sendo feita pela regra pura de `customer360.ts`, sobre
 *    estes registros.
 * 3. É **puro**: só tipos e validação de borda, sem I/O, sem env, sem React.
 *    Endereço registrado no ADR-0007.
 */
import { assertCrmCompany, assertCrmCustomer, assertCrmOpportunity, assertCrmQuote, assertCrmSegment, assertCrmTask } from "./entities.js";
import { isOrderOrigin, isOrderState } from "./order-state-machine.js";
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Telefone de canal: só dígitos, com `+`/separadores opcionais (E.164 e afins). */
const PHONE_PATTERN = /^\+?[0-9][0-9\s().-]*$/;
/** Dígitos de um telefone; `""` para o que não é telefone de canal. */
function phoneDigits(value) {
    if (!PHONE_PATTERN.test(value))
        return "";
    const digits = value.replace(/\D+/g, "");
    // Oito dígitos é o menor telefone brasileiro sem DDD; abaixo disso o valor
    // não é identificável como telefone e segue como id de cadastro.
    return digits.length >= 8 ? digits : "";
}
/**
 * Classifica o identificador do cliente que chegou na rota. `null` para
 * entrada vazia — ausência de seletor é 400 no chamador, não um palpite.
 */
export function classifyCrmCustomerSelector(raw) {
    const value = raw.trim();
    if (!value)
        return null;
    if (UUID_PATTERN.test(value))
        return { kind: "canonical_id", value };
    if (value.includes("@"))
        return { kind: "identity", identityKind: "email", value };
    if (phoneDigits(value))
        return { kind: "identity", identityKind: "whatsapp_phone", value };
    // Resto: id de cadastro (inclui os ids de seed de dev/E2E, que não são uuid).
    return { kind: "canonical_id", value };
}
/**
 * Chave comparável de uma identidade de canal.
 *
 * Telefone é comparado por dígitos porque a mesma identidade aparece com e sem
 * `+`/separadores dependendo de quem gravou (`+55 11 98765-4321` no cadastro,
 * `5511987654321` no inbound); e-mail e demais identificadores são comparados
 * por texto em minúsculas. É normalização de BORDA, não regra de negócio: a
 * resolução de identidade continua sendo da 0020.
 */
function channelKey(value) {
    const digits = phoneDigits(value);
    return digits || value.trim().toLowerCase();
}
/**
 * Identificadores pelos quais um atendimento pode estar guardado para ESTE
 * cliente: o id canônico e as identidades **verificadas** dele (0020).
 *
 * São exatamente as chaves que o runtime usa para buscar as conversas
 * (`CrmStore.listConversationsOfCustomer`); repetir a lista aqui é o que
 * permite à borda recusar uma conversa que não é deste cliente sem consultar
 * nada.
 */
export function crmConversationKeysOf(customer) {
    const keys = new Set([channelKey(customer.id)]);
    for (const identity of customer.identities) {
        // Identidade NÃO verificada não vincula atendimento: é o mesmo critério do
        // `p_require_verified` da 0020.
        if (identity.verified)
            keys.add(channelKey(identity.value));
    }
    return keys;
}
function fail(entity, reason) {
    throw new Error(`Invalid ${entity}: ${reason}`);
}
function isRecord(value) {
    return typeof value === "object" && value !== null;
}
/**
 * Recorte do pedido unificado da Task 12 no formato do 360.
 *
 * É só projeção — nenhum campo é calculado ou preenchido. Existe para o
 * caminho que ainda carrega a lista completa de pedidos (`/api/platform/orders`)
 * produzir exatamente a MESMA estrutura que o runtime publica em `/api/crm/360`:
 * a tela de 360 renderiza um formato, não dois.
 */
export function toCrm360Order(order) {
    return {
        id: order.id,
        origin: order.origin,
        externalId: order.externalId,
        checkoutOrderId: order.checkoutOrderId,
        conversationId: order.conversationId,
        customerId: order.customerId,
        customer: order.customer,
        state: order.state,
        totalCents: order.totalCents,
        placedAt: order.placedAt
    };
}
export function assertCrm360Order(value) {
    if (!isRecord(value))
        fail("Crm360Order", "payload is not an object");
    if (typeof value.id !== "string" || !value.id)
        fail("Crm360Order", "missing id");
    // Origem e estado reconhecidos: pedido sem procedência não entra no
    // histórico de compras (mesma exigência de `assertPlatformUnifiedOrder`).
    if (!isOrderOrigin(value.origin))
        fail("Crm360Order", `unknown origin ${String(value.origin)}`);
    if (!isOrderState(value.state))
        fail("Crm360Order", `unknown state ${String(value.state)}`);
    if (typeof value.externalId !== "string")
        fail("Crm360Order", "missing externalId");
    if (typeof value.placedAt !== "string")
        fail("Crm360Order", "missing placedAt");
    if (value.totalCents !== null && typeof value.totalCents !== "number") {
        fail("Crm360Order", "totalCents must be a number or null");
    }
    if (value.customer !== null && !isRecord(value.customer)) {
        fail("Crm360Order", "customer must be an object or null");
    }
    return value;
}
export function assertCrm360Conversation(value) {
    if (!isRecord(value))
        fail("Crm360Conversation", "payload is not an object");
    if (typeof value.id !== "string" || !value.id)
        fail("Crm360Conversation", "missing id");
    if (typeof value.customerId !== "string" || !value.customerId)
        fail("Crm360Conversation", "missing customerId");
    if (typeof value.leadScore !== "number")
        fail("Crm360Conversation", "missing leadScore");
    if (typeof value.updatedAt !== "string")
        fail("Crm360Conversation", "missing updatedAt");
    if (typeof value.messageCount !== "number")
        fail("Crm360Conversation", "missing messageCount");
    return value;
}
/**
 * Validação de borda do payload do 360.
 *
 * Exige o cliente canônico resolvido e como ele foi resolvido: sem isso a tela
 * não saberia dizer se está mostrando o cadastro ou um palpite por telefone —
 * e essa distinção é o que a Task 13 tem de preservar. Registro de outro
 * cliente no payload é erro de borda, não linha a exibir: o 360 inteiro perde
 * sentido se duas pessoas aparecerem na mesma tela. Isso vale para TODAS as
 * seis fontes, conversas incluídas: pedido, negociação, proposta e tarefa são
 * exigidos no id canônico, e conversa numa das chaves deste cliente (id
 * canônico ou identidade verificada da 0020).
 */
export function assertCrm360Payload(value) {
    if (!isRecord(value))
        fail("Crm360Payload", "payload is not an object");
    if (value.resolvedBy !== "canonical_id" && value.resolvedBy !== "identity") {
        fail("Crm360Payload", `unknown resolvedBy ${String(value.resolvedBy)}`);
    }
    const customer = assertCrmCustomer(value.customer);
    const company = value.company === null ? null : assertCrmCompany(value.company);
    if (!Array.isArray(value.orders))
        fail("Crm360Payload", "orders must be an array");
    if (!Array.isArray(value.conversations))
        fail("Crm360Payload", "conversations must be an array");
    if (!Array.isArray(value.opportunities))
        fail("Crm360Payload", "opportunities must be an array");
    if (!Array.isArray(value.quotes))
        fail("Crm360Payload", "quotes must be an array");
    if (!Array.isArray(value.tasks))
        fail("Crm360Payload", "tasks must be an array");
    if (!Array.isArray(value.segments))
        fail("Crm360Payload", "segments must be an array");
    const orders = value.orders.map(assertCrm360Order);
    const conversations = value.conversations.map(assertCrm360Conversation);
    const opportunities = value.opportunities.map(assertCrmOpportunity);
    const quotes = value.quotes.map(assertCrmQuote);
    const tasks = value.tasks.map(assertCrmTask);
    const segments = value.segments.map(assertCrmSegment);
    for (const order of orders) {
        if (order.customerId !== null && order.customerId !== customer.id) {
            fail("Crm360Payload", `order ${order.id} belongs to another customer`);
        }
    }
    for (const record of [...opportunities, ...quotes, ...tasks]) {
        if (record.customerId !== customer.id) {
            fail("Crm360Payload", `record ${record.id} belongs to another customer`);
        }
    }
    // Conversa também é registro do cliente (correção da revisão da Iteração 3,
    // finding 4). O atendimento é guardado sob o id canônico OU sob a identidade
    // do canal (no WhatsApp, o telefone do inbound) — então aqui não dá para
    // exigir `customer.id`. Dá para exigir que seja UMA DAS CHAVES DESTE
    // cliente: o id canônico ou uma identidade verificada da 0020. Sem esta
    // checagem, um `customerId` estranho passaria pela borda e a tela
    // selecionaria, pelo id recebido, o atendimento de outra pessoa.
    const conversationKeys = crmConversationKeysOf(customer);
    for (const conversation of conversations) {
        if (!conversationKeys.has(channelKey(conversation.customerId))) {
            fail("Crm360Payload", `conversation ${conversation.id} belongs to another customer`);
        }
    }
    return { resolvedBy: value.resolvedBy, customer, company, orders, conversations, opportunities, quotes, tasks, segments };
}
